import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { api } from '../lib/api';
import { useAppSelector } from '../hooks/redux';

interface SolarSystem {
  id: number;
  nameEn: string;
  securityStatus: number | null;
  costIndices: {
    manufacturing?: number;
  } | null;
  costIndicesUpdatedAt: string | null;
}

interface StructureType {
  id: number;
  nameEn: string;
  iconId: number | null;
}

interface Structure {
  id: string;
  name: string;
  solarSystemId: number;
  structureTypeId: number;
  materialRig: string;
  timeRig: string;
  solarSystem: SolarSystem;
  structureType: StructureType;
  createdAt: string;
}

function Structures() {
  const { initialized } = useAppSelector((state) => state.auth);
  const [structures, setStructures] = useState<Structure[]>([]);
  const [structureTypes, setStructureTypes] = useState<StructureType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStructure, setEditingStructure] = useState<Structure | null>(null);

  // Dialog form state
  const [formName, setFormName] = useState('');
  const [formSolarSystem, setFormSolarSystem] = useState<SolarSystem | null>(null);
  const [formSolarSystemSearch, setFormSolarSystemSearch] = useState('');
  const [solarSystemSuggestions, setSolarSystemSuggestions] = useState<SolarSystem[]>([]);
  const [showSolarSystemDropdown, setShowSolarSystemDropdown] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [formStructureType, setFormStructureType] = useState<StructureType | null>(null);
  const [formMaterialRig, setFormMaterialRig] = useState<string>('None');
  const [formTimeRig, setFormTimeRig] = useState<string>('None');
  const [formFacilityTax, setFormFacilityTax] = useState<number>(0);
  const [updatingCostIndices, setUpdatingCostIndices] = useState(false);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Only fetch when auth is fully initialized (session loaded and header set)
    if (initialized) {
      fetchStructures();
      fetchStructureTypes();
    }
  }, [initialized]);

  useEffect(() => {
    // Reset selected index when suggestions change
    setSelectedSuggestionIndex(-1);
  }, [solarSystemSuggestions]);

  const fetchStructures = async () => {
    try {
      setLoading(true);
      const response = await api.get('/structures');
      setStructures(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch structures:', error);
      setLoading(false);
    }
  };

  const fetchStructureTypes = async () => {
    try {
      const response = await api.get('/structures/types');
      setStructureTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch structure types:', error);
    }
  };

  const searchSolarSystems = async (query: string) => {
    if (query.length < 2) {
      setSolarSystemSuggestions([]);
      return;
    }

    try {
      const response = await api.get('/solar-systems/search', {
        params: { q: query },
      });
      setSolarSystemSuggestions(response.data);
      setShowSolarSystemDropdown(true);
    } catch (error) {
      console.error('Failed to search solar systems:', error);
      setSolarSystemSuggestions([]);
    }
  };

  const selectSolarSystem = (system: SolarSystem) => {
    setFormSolarSystem(system);
    setFormSolarSystemSearch(system.nameEn);
    setShowSolarSystemDropdown(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleSolarSystemKeyDown = (e: React.KeyboardEvent) => {
    if (!showSolarSystemDropdown || solarSystemSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) =>
          prev < solarSystemSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0) {
          selectSolarSystem(solarSystemSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSolarSystemDropdown(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  const updateCostIndices = async () => {
    if (!formSolarSystem) return;

    try {
      setUpdatingCostIndices(true);
      const response = await api.post(
        `/solar-systems/${formSolarSystem.id}/update-cost-indices`,
        {}
      );
      setFormSolarSystem({ ...formSolarSystem, ...response.data });
      setUpdatingCostIndices(false);
    } catch (error) {
      console.error('Failed to update cost indices:', error);
      setUpdatingCostIndices(false);
      alert('Failed to fetch cost indices from ESI');
    }
  };


  const getFacilityMaterialModifier = (structureTypeName: string): number => {
    if (structureTypeName.toLowerCase().includes('npc')) return 0;
    return 0.01; // 1% for all player structures
  };

  const getFacilityTimeModifier = (structureTypeName: string): number => {
    const name = structureTypeName.toLowerCase();
    if (name.includes('npc')) return 0;
    if (name.includes('medium') || name.includes('raitaru')) return 0.15;
    if (name.includes('large') || name.includes('azbel')) return 0.20;
    if (name.includes('xlarge') || name.includes('sotiyo')) return 0.30;
    return 0.15; // Default to medium
  };

  const openCreateDialog = () => {
    setEditingStructure(null);
    setFormName('');
    setFormSolarSystem(null);
    setFormSolarSystemSearch('');
    setFormStructureType(null);
    setFormMaterialRig('None');
    setFormTimeRig('None');
    setFormFacilityTax(0);
    setShowDialog(true);
  };

  const openEditDialog = (structure: Structure) => {
    setEditingStructure(structure);
    setFormName(structure.name);
    setFormSolarSystem(structure.solarSystem);
    setFormSolarSystemSearch(structure.solarSystem.nameEn);
    setFormStructureType(structure.structureType);
    setFormMaterialRig(structure.materialRig);
    setFormTimeRig(structure.timeRig);
    setFormFacilityTax(((structure as any).facilityTax ?? 0) * 100); // Convert decimal to percentage
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingStructure(null);
  };

  const handleSave = async () => {
    if (!formName || !formSolarSystem || !formStructureType) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      if (editingStructure) {
        // Update existing structure
        await api.put(`/structures/${editingStructure.id}`, {
          name: formName,
          solarSystemId: formSolarSystem.id,
          structureTypeId: formStructureType.id,
          materialRig: formMaterialRig,
          timeRig: formTimeRig,
          facilityTax: formFacilityTax / 100, // Convert percentage to decimal
        });
      } else {
        // Create new structure
        await api.post('/structures', {
          name: formName,
          solarSystemId: formSolarSystem.id,
          structureTypeId: formStructureType.id,
          materialRig: formMaterialRig,
          timeRig: formTimeRig,
          facilityTax: formFacilityTax / 100, // Convert percentage to decimal
        });
      }
      setSaving(false);
      closeDialog();
      fetchStructures();
    } catch (error) {
      console.error('Failed to save structure:', error);
      setSaving(false);
      alert('Failed to save structure');
    }
  };

  const handleDelete = async (structureId: string) => {
    if (!confirm('Are you sure you want to delete this structure?')) {
      return;
    }

    try {
      await api.delete(`/structures/${structureId}`);
      fetchStructures();
    } catch (error) {
      console.error('Failed to delete structure:', error);
      alert('Failed to delete structure');
    }
  };


  const handleRefreshAllCostIndices = async () => {
    try {
      setRefreshingAll(true);
      // Get unique solar system IDs from structures
      const uniqueSolarSystemIds = [...new Set(structures.map(s => s.solarSystemId))];

      // Update cost indices for each unique solar system
      await Promise.all(
        uniqueSolarSystemIds.map(systemId =>
          api.post(`/solar-systems/${systemId}/update-cost-indices`, {})
        )
      );

      // Refresh the structures list to get updated data
      await fetchStructures();
      setRefreshingAll(false);
    } catch (error) {
      console.error('Failed to refresh cost indices:', error);
      setRefreshingAll(false);
      alert('Failed to refresh some cost indices from ESI');
    }
  };

  const materialModifier = formStructureType ? getFacilityMaterialModifier(formStructureType.nameEn) : 0;
  const timeModifier = formStructureType ? getFacilityTimeModifier(formStructureType.nameEn) : 0;

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">Structures</h1>
          <div className="flex gap-3">
            <button
              onClick={handleRefreshAllCostIndices}
              disabled={refreshingAll || structures.length === 0}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              title="Refresh all cost indices"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshingAll ? 'Refreshing...' : 'Refresh All'}
            </button>
            <button
              onClick={openCreateDialog}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Create Structure
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-400 py-12">Loading structures...</div>
        ) : structures.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            No structures yet. Click "Create Structure" to add one.
          </div>
        ) : (
          <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-800 border-b border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Icon</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">System</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Structure Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Rigs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Cost Index</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Updated</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {structures.map((structure) => (
                  <tr key={structure.id} className="hover:bg-gray-800 transition-colors">
                    <td className="px-4 py-3">
                      <img
                        src={`https://images.evetech.net/types/${structure.structureTypeId}/icon?size=64`}
                        alt={structure.structureType.nameEn}
                        className="w-10 h-10 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-white">{structure.solarSystem.nameEn}</td>
                    <td className="px-4 py-3 text-sm text-white font-medium">{structure.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">{structure.structureType.nameEn}</td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 w-4">M:</span>
                          <span className={structure.materialRig === 'None' ? 'text-gray-500' : 'text-blue-400'}>{structure.materialRig}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 w-4">T:</span>
                          <span className={structure.timeRig === 'None' ? 'text-gray-500' : 'text-blue-400'}>{structure.timeRig}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {structure.solarSystem.costIndices?.manufacturing !== undefined
                        ? `${(structure.solarSystem.costIndices.manufacturing * 100).toFixed(2)}%`
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400">
                      {structure.solarSystem.costIndicesUpdatedAt
                        ? new Date(structure.solarSystem.costIndicesUpdatedAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditDialog(structure)}
                          className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
                          title="Edit structure"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(structure.id)}
                          className="p-1 text-red-400 hover:text-red-300 transition-colors"
                          title="Delete structure"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create/Edit Structure Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-6">
                {editingStructure ? 'Edit Structure' : 'Create Structure'}
              </h2>

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Name</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Enter structure name..."
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Solar System */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Solar System</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formSolarSystemSearch}
                    onChange={(e) => {
                      setFormSolarSystemSearch(e.target.value);
                      searchSolarSystems(e.target.value);
                    }}
                    onKeyDown={handleSolarSystemKeyDown}
                    onFocus={() => {
                      if (solarSystemSuggestions.length > 0) {
                        setShowSolarSystemDropdown(true);
                      }
                    }}
                    placeholder="Start typing to search..."
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showSolarSystemDropdown && solarSystemSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {solarSystemSuggestions.map((sys, index) => (
                        <button
                          key={sys.id}
                          onClick={() => selectSolarSystem(sys)}
                          className={`w-full px-3 py-2 text-left text-sm text-white transition-colors flex items-center justify-between ${
                            index === selectedSuggestionIndex
                              ? 'bg-blue-600'
                              : 'hover:bg-gray-700'
                          }`}
                        >
                          <span>{sys.nameEn}</span>
                          <span className="text-xs text-gray-400">
                            {sys.securityStatus !== null ? sys.securityStatus.toFixed(2) : 'N/A'}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Structure Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Structure Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {structureTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setFormStructureType(type)}
                      className={`flex items-center gap-2 rounded-lg border transition-colors overflow-hidden ${
                        formStructureType?.id === type.id
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      <img
                        src={`https://images.evetech.net/types/${type.id}/icon?size=64`}
                        alt=""
                        className="h-full w-auto flex-shrink-0"
                      />
                      <span className="truncate pr-3">{type.nameEn}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Material Rig */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Material Rig</label>
                <div className="flex gap-2">
                  {['None', 'T1', 'T2'].map((rig) => (
                    <button
                      key={rig}
                      onClick={() => setFormMaterialRig(rig)}
                      className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                        formMaterialRig === rig
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      {rig}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Rig */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">Time Rig</label>
                <div className="flex gap-2">
                  {['None', 'T1', 'T2'].map((rig) => (
                    <button
                      key={rig}
                      onClick={() => setFormTimeRig(rig)}
                      className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                        formTimeRig === rig
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                      }`}
                    >
                      {rig}
                    </button>
                  ))}
                </div>
              </div>

              {/* Facility Tax */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Facility Tax (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formFacilityTax}
                  onChange={(e) => setFormFacilityTax(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter facility tax percentage (e.g., 5 for 5%)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Enter the tax percentage charged by the structure owner
                </p>
              </div>

              {/* Modifiers Section */}
              {formSolarSystem && formStructureType && (
                <div className="mb-6 bg-gray-900 rounded-lg border border-gray-700 p-4">
                  <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Applied Modifiers
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Facility Material Bonus:</span>
                      <span className="text-green-400 font-semibold">
                        -{(materialModifier * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Facility Time Bonus:</span>
                      <span className="text-green-400 font-semibold">
                        -{(timeModifier * 100).toFixed(1)}%
                      </span>
                    </div>
                    {formSolarSystem.costIndices?.manufacturing !== undefined && (
                      <div className="flex justify-between pt-2 border-t border-gray-700">
                        <span className="text-gray-400">Manufacturing Cost Index:</span>
                        <span className="text-white font-semibold">
                          {(formSolarSystem.costIndices.manufacturing * 100).toFixed(4)}%
                        </span>
                      </div>
                    )}
                  </div>

                  {formSolarSystem && (
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={updateCostIndices}
                        disabled={updatingCostIndices}
                        className="px-3 py-1 text-xs bg-gray-800 text-gray-300 rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                      >
                        <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {updatingCostIndices ? 'Refreshing...' : 'Refresh Cost Index'}
                      </button>
                      {formSolarSystem.costIndicesUpdatedAt && (
                        <span className="text-xs text-gray-500">
                          Updated: {new Date(formSolarSystem.costIndicesUpdatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={closeDialog}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Structures;
